import { Text } from '@thanx/uikit/text'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import React from 'react'
import Nav from 'react-bootstrap-five/Nav'

type Props = {
  state: string
  title: React.ReactNode
  disabled?: boolean
  // Explains WHY the tab is disabled, shown on hover/focus. Ignored unless
  // `disabled` is also set. A greyed-out tab with no explanation leaves the
  // viewer guessing whether it's a permission gap, a missing feature, or a
  // bug — the same reason nav links carry a reason.
  disabledReason?: string
  // Optional render slot for per-tab affordances (e.g. an escalation CountBadge).
  // Rendered after the title so it doesn't inherit SmallCaps uppercase/color.
  badge?: React.ReactNode
  // Pairs with `TabNav`'s `compact` prop for narrow containers (the 340px
  // feedback inbox rail) — swaps the uppercase, letter-spaced SmallCaps1
  // label and full-width `px-m` padding for a smaller, sentence-case label
  // (`BodyText4`) and tighter padding, so three tabs fit on one line without
  // overflowing. Default (false) leaves every other TabItem consumer
  // pixel-identical.
  compact?: boolean
}

const TabItem = ({
  state,
  title,
  disabled,
  disabledReason,
  badge,
  compact = false,
}: Props) => {
  return (
    <Nav.Item key={state}>
      {/* The tooltip target has to sit OUTSIDE the link: Bootstrap sets
          `pointer-events: none` on `.nav-link.disabled`, so a disabled tab
          never fires the hover itself. PermissionTooltip's wrapper takes the
          hover and carries its own tabIndex, which also keeps the reason
          reachable by keyboard (a11y checklist: a disabled control's tooltip
          must not be hover-only). `inline` so the wrapper doesn't stretch the
          tab across the nav row. Renders children untouched when not
          disabled or when no reason is supplied. */}
      <PermissionTooltip
        disabled={disabled}
        reason={disabledReason}
        id={`tab-${state}-disabled-reason`}
        placement="bottom"
        inline
        // The tab's own title must stay the accessible name — the default
        // role="note"/aria-label wrapper would replace it with the reason
        // alone, leaving a screen-reader user unable to tell WHICH tab is
        // locked (WCAG 2.1 4.1.2). See PermissionTooltip's srOnlyReason doc.
        srOnlyReason
      >
        <Nav.Link
          className={`flex-justify-center ${compact ? 'px-xs' : 'px-m'}`}
          eventKey={state}
          disabled={disabled}
        >
          {compact ? (
            <Text.BodyText4 color="inherit" bold={true}>
              {title}
            </Text.BodyText4>
          ) : (
            <Text.SmallCaps1 color="inherit" bold={true}>
              {title}
            </Text.SmallCaps1>
          )}
          {badge}
        </Nav.Link>
      </PermissionTooltip>
    </Nav.Item>
  )
}

export default TabItem
